import { Typography } from '@mui/material';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  FormHelperText,
  Skeleton,
  Box,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import MainCard from 'components/MainCard';

import * as Yup from 'yup';
import { Formik } from 'formik';

import { useSelector, useDispatch } from 'react-redux';
import * as FirestoreDataService from './../../services/FirestoreDataService';
import { showAppAlert } from 'store/reducers/appreducer';
import { resolvePartnerModifySettingsAllowed } from 'utils/partnerUiCapabilities';
import { useEffect, useState } from 'react';

import { setDoc, doc } from 'firebase/firestore';
import { db } from 'services/firebase';
import OfflineDialog from 'components/OfflineDialog';

const INITIAL_VALUES = {
  current_working_mode: 3,
  lazy_report_interval: '',
  report_interval: '',
  heartbeat_interval: 60,
  heartbeat_enable: false
};

const WorkModeSettingsPage = () => {
  const workingModes = [1, 2, 3, 4, 5, 6];

  const { selectedDevice, appUser } = useSelector((state) => state.appreducer);
  const [workModeSettings, setWorkModeSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const status = await FirestoreDataService.getDeviceStatus(selectedDevice);

      if (status.exists()) {
        const last_online_time = status.data()?.last_online_time;
        const offlineThreshold = 5 * 60 * 1000; // 5 minutes;
        const currentTime = Date.now();
        const deviceStatus = currentTime - last_online_time < offlineThreshold ? 'online' : 'offline';

        if (deviceStatus === 'offline') {
          setIsDialogOpen(true);
        }
      }
    })();
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (!selectedDevice) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [deviceStatusSnap, deviceSettingsSnap] = await Promise.all([
          FirestoreDataService.getDeviceStatus(selectedDevice),
          FirestoreDataService.getDeviceSettings(selectedDevice)
        ]);

        let currentWorkingMode;
        let workModeSettingsObject;
        let lazyReportInterval;
        let reportInterval;
        let heartBeatInterval;
        let heartBeatEnable;

        if (deviceStatusSnap.exists() && deviceStatusSnap.data().work_mode) {
          currentWorkingMode = deviceStatusSnap.data().work_mode;
        }

        if (deviceSettingsSnap.exists()) {
          const deviceSettingsData = deviceSettingsSnap.data();
          workModeSettingsObject = deviceSettingsData.work_mode;

          if ([4, 5].includes(currentWorkingMode)) {
            reportInterval = workModeSettingsObject?.[currentWorkingMode]?.interval;
          } else if (deviceSettingsData.time_interval) {
            const timeInterval = deviceSettingsData.time_interval;
            reportInterval = timeInterval.report_interval;
            lazyReportInterval = timeInterval.lazy_report_interval;
            heartBeatInterval = timeInterval.heartbeat_interval;
            heartBeatEnable = timeInterval.heartbeat_enable;
          }
        }

        setWorkModeSettings({
          current_working_mode: currentWorkingMode || 3,
          lazy_report_interval: lazyReportInterval || '',
          report_interval: reportInterval || '',
          heartbeat_interval: heartBeatInterval || 60,
          heartbeat_enable: heartBeatEnable || false
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to get work mode settings!' }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDevice, dispatch]);

  return (
    <Grid item xs={12} sm={12} md={8} lg={6}>
      <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
        <Typography variant="h6" color="textSecondary">
          Work Mode Settings
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="rectangular" animation="wave" height={230} />
          </>
        ) : (
          <Formik
            initialValues={workModeSettings ? workModeSettings : INITIAL_VALUES}
            validationSchema={Yup.object().shape({
              current_working_mode: Yup.number(),
              report_interval: Yup.number().required('Enter time in seconds i.e. 10s - 3600s'),
              lazy_report_interval: Yup.number().required('Enter time in seconds i.e. 10s - 3600s'),
              heartbeat_interval: Yup.number().required('Enter time in seconds i.e. 10s - 3600s'),
              heartbeat_enable: Yup.boolean().required()
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                setStatus({ success: false });
                setSubmitting(true);

                // let formValues = { ...values };
                let payload = {};

                const canModifySettings = resolvePartnerModifySettingsAllowed(appUser);

                if (!canModifySettings) {
                  console.log('cant save settings');
                  dispatch(
                    showAppAlert({
                      show: true,
                      alertType: 'error',
                      alertMessage: 'Error: You do not have permission to save these settings.'
                    })
                  );
                  return;
                }

                /* 
                if (values.current_working_mode === 1) {
                  payload = { current_working_mode: values.current_working_mode };
                }

                if (values.current_working_mode === 2 || values.current_working_mode === 6)
                {
                  payload = {...values}
                }

                if (values.current_working_mode === 3) {
                  payload = {current_working_mode: values.current_working_mode, report_interval: values.report_interval}
                }

                if (values.current_working_mode === 4 || values.current_working_mode === 5) {
                  payload = {current_working_mode: values.current_working_mode, interval: values.report_interval};
                }

                */
                payload = {
                  work_mode: values.current_working_mode,
                  report_interval: values.report_interval ? values.report_interval : 0,
                  lazy_report_interval: values.lazy_report_interval ? values.lazy_report_interval : 0,
                  heartbeat_interval: values.heartbeat_interval ? values.heartbeat_interval : 60,
                  heartbeat_enable: values.heartbeat_enable && values.heartbeat_enable
                };

                // console.log('payload', payload);
                // console.log('payload', payload);

                const tasksRef = doc(db, 'tasks', selectedDevice);
                setDoc(tasksRef, {
                  device_id: selectedDevice,
                  type: 'work_mode',
                  user_id: appUser.email ? appUser.email : '-',
                  old_value: workModeSettings,
                  payload
                })
                  .then((response) => {
                    console.log('saved tasks!', response);
                    const msg = 'Work mode settings saved successfully !';
                    dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                  })
                  .catch((e) => {
                    const msg = 'Failed to save work mode settings!';
                    console.log(msg, e);
                    dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                  });
              } catch (err) {
                console.log('error', err);
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to save work mode settings' }));
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box sx={{ mt: 2 }}>
                  <Grid item xs={12} justifyContent="space-between" alignItems="center" sx={{ display: 'flex' }}>
                    <Typography variant="caption" color="textSecondary">
                      Set working mode for device
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Stack spacing={1} style={{ marginTop: '16px' }}>
                      {
                        <FormControl>
                          <InputLabel id="number-label" sx={{ lineHeight: '1.5px' }}>
                            Select Working Mode
                          </InputLabel>
                          <Select
                            labelId="number-label"
                            id="number-select"
                            name={`current_working_mode`}
                            value={values.current_working_mode}
                            onChange={handleChange}
                          >
                            {workingModes.map((number) => {
                              return (
                                <MenuItem key={number} value={number}>
                                  {number}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      }
                    </Stack>
                    {values.current_working_mode !== 1 && (
                      <>
                        <Stack spacing={1} style={{ marginTop: '16px' }}>
                          <TextField
                            // disabled={true}
                            fullWidth
                            id="report_interval"
                            type="number"
                            value={values.report_interval}
                            name="report_interval"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="10s - 3600s"
                            label="Reporting Interval when moving."
                            error={Boolean(errors.report_interval)}
                            helperText={errors.report_interval}
                            InputLabelProps={{
                              style: {
                                lineHeight: '1.5em'
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                            }}
                          />
                          {touched.name && errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
                        </Stack>
                      </>
                    )}

                    {[2, 6].includes(values.current_working_mode) && (
                      <>
                        <Stack spacing={1} style={{ marginTop: '16px' }}>
                          <TextField
                            // disabled={true}
                            fullWidth
                            id="lazy_report_interval"
                            type="number"
                            value={values.lazy_report_interval}
                            name="lazy_report_interval"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="10s - 3600s"
                            label="Reporting Interval when not moving."
                            error={Boolean(errors.lazy_report_interval)}
                            helperText={errors.lazy_report_interval}
                            InputLabelProps={{
                              style: {
                                lineHeight: '1.5em'
                              }
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                            }}
                          />
                          {touched.name && errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
                        </Stack>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12} justifyContent="space-between" alignItems="center" sx={{ display: 'flex', mt: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Set Heart Beat / Ping Interval for Device
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <>
                      <Stack spacing={1} style={{ marginTop: '16px' }}>
                        <TextField
                          // disabled={true}
                          fullWidth
                          id="heartbeat_interval"
                          type="number"
                          value={values.heartbeat_interval}
                          name="heartbeat_interval"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="10s - 3600s"
                          label="Device Ping / Heartbeat Interval"
                          error={Boolean(errors.heartbeat_interval)}
                          helperText={errors.heartbeat_interval}
                          InputLabelProps={{
                            style: {
                              lineHeight: '1.5em'
                            }
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                          }}
                        />
                        {touched.name && errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
                      </Stack>
                      <Stack spacing={1} style={{ marginTop: '16px' }}>
                        {/* todo: want to add mui switch */}
                        <Typography variant="caption" color="textSecondary">
                          Enable Heart Beat / Ping Interval for Device
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch checked={values.heartbeat_enable} onChange={handleChange} name="heartbeat_enable" color="primary" />
                          }
                          label={values.heartbeat_enable ? 'Enabled' : 'Disabled'}
                        />
                      </Stack>
                    </>
                  </Grid>
                  <Button
                    disableElevation
                    style={{ float: 'right', marginTop: 16, marginBottom: 16 }}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        )}
      </MainCard>
      <OfflineDialog isOpen={isDialogOpen} handleClose={handleDialogClose}></OfflineDialog>
    </Grid>
  );
};

export default WorkModeSettingsPage;
